import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { searchCatalogExercises } from "./funtionsBD/Mainservice";
import { normalizeAppLang } from "../utils/appLanguages";
import { localizedExerciseName, localizedField } from "../utils/localizedCatalogFields";

export function catalogExerciseLabel(ex, lang = "en") {
  return localizedExerciseName(ex, lang) || ex?.ex_id || "";
}

export function catalogExerciseDescription(ex, lang = "en") {
  if (!ex) return "";
  const l = normalizeAppLang(lang);
  const fields = [
    localizedField(ex, "ex_objective", l),
    localizedField(ex, "ex_instruction", l),
    localizedField(ex, "ex_process", l),
  ];
  for (const f of fields) {
    if (f && String(f).trim()) return String(f).trim();
  }
  return "";
}

function truncate(text, max = 90) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * Searchable catalog-exercise picker. Search matches name and description.
 */
export default function CatalogExercisePicker({
  value = null,
  onChange,
  disabled = false,
  label,
  helperText,
}) {
  const { t, i18n } = useTranslation();
  const lang = normalizeAppLang(i18n.language);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = inputValue.trim();
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { page: 1, limit: 25 };
        if (q) params.search = q;
        const res = await searchCatalogExercises(params);
        setOptions(res.data?.data?.exercises || []);
      } catch (err) {
        console.error("Catalog exercise search failed:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, q ? 280 : 0);
    return () => clearTimeout(handle);
  }, [inputValue]);

  const mergedOptions = useMemo(() => {
    const list = Array.isArray(options) ? [...options] : [];
    if (value?.ex_id && !list.some((ex) => String(ex.ex_id) === String(value.ex_id))) {
      list.unshift(value);
    }
    return list;
  }, [options, value]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      options={mergedOptions}
      loading={loading}
      value={value}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(a, b) => String(a?.ex_id || "") === String(b?.ex_id || "")}
      getOptionLabel={(opt) => catalogExerciseLabel(opt, lang)}
      onChange={(_e, next) => onChange?.(next || null)}
      onInputChange={(_e, next, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(next);
      }}
      noOptionsText={t("noCatalogExercisesFound") || "No catalog exercises found"}
      renderOption={(props, opt) => {
        const desc = catalogExerciseDescription(opt, lang);
        return (
          <li {...props} key={opt.ex_id}>
            <Box sx={{ py: 0.25, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {catalogExerciseLabel(opt, lang)}
              </Typography>
              {desc ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {truncate(desc, 110)}
                </Typography>
              ) : null}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || t("baseExercise") || "Base exercise"}
          helperText={
            helperText ||
            t(
              "baseExerciseHelp",
              "Catalog exercise used for name, description and video. Sequences stay as defined below."
            )
          }
          placeholder={t("searchExercisesByNameOrDescription") || "Search by name or description…"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
